import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import Chatbot from '@/components/shared/Chatbot';
import { Button } from '@/components/ui/button';
import { 
  GraduationCap, Building2, Users, Award, Calendar, Target, Eye,
  CheckCircle, ArrowRight, Quote, Briefcase, Shield, TrendingUp,
  BookOpen, Handshake, Star, Clock, MapPin, Phone
} from 'lucide-react';

const AboutPage = () => {
  const timelineRef = useRef(null);

  // Scroll animation for timeline
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fadeInUp');
          }
        });
      },
      { threshold: 0.1 }
    );

    const timelineItems = document.querySelectorAll('.timeline-item');
    timelineItems.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  const milestones = [
    { year: '2011', title: 'Founded', description: 'Iqra Skills established with a vision to transform lives through skill development', icon: Star },
    { year: '2013', title: 'STAR Scheme', description: 'Started Standard Training Assessment and Reward Scheme implementation', icon: Award },
    { year: '2014', title: 'NDLM Program', description: 'National Digital Literacy Mission by DeitY', icon: BookOpen },
    { year: '2015', title: 'GJSCI Partnership', description: 'Partnership with Gem & Jewellery Skill Council established', icon: Handshake },
    { year: '2015', title: 'ESSCI Partnership', description: 'Partnership with Electronics Sector Skills Council of India', icon: Handshake },
    { year: '2015', title: 'PMKVY 1.0', description: 'Started Pradhan Mantri Kaushal Vikas Yojana 1.0', icon: GraduationCap },
    { year: '2017', title: 'PMKVY 2.0', description: 'Expanded operations with PMKVY 2.0 program', icon: TrendingUp },
    { year: '2018', title: 'ESDM Program', description: 'Electronics System Design & Manufacturing under MeitY', icon: Building2 },
    { year: '2019', title: 'PMKVY 2.0', description: 'Continued PMKVY 2.0 with expanded reach across states', icon: MapPin },
    { year: '2021', title: 'C4W Program', description: 'Centres for Women program with MSDE', icon: Users },
    { year: '2022', title: 'RAJKVIK', description: 'Rajasthan Kaushal Vikas Yojana implementation', icon: Award },
    { year: '2022', title: 'TPA Affiliation', description: 'Third Party Aggregators affiliation with MSDE', icon: Shield },
    { year: '2022', title: 'PMKVY 3.0', description: 'Launched PMKVY 3.0 training programs', icon: GraduationCap },
    { year: '2022', title: 'NULM Program', description: 'National Urban Livelihoods Mission started', icon: Briefcase },
    { year: '2022', title: 'NSDC Partnership', description: 'Placement & Mobilization Partner with NSDC', icon: Handshake },
    { year: '2024', title: 'PMKVY 4.0', description: 'Latest phase of PMKVY program launched', icon: TrendingUp },
    { year: '2025', title: 'ESDP-MSME', description: 'Entrepreneurship & Skill Development Program with MSME', icon: Star }
  ];

  const impactStats = [
    { number: '25,000+', label: 'Students Trained', icon: GraduationCap },
    { number: '100+', label: 'Training Centers', icon: Building2 },
    { number: '15+', label: 'Years Experience', icon: Calendar },
    { number: '15+', label: 'Govt Programs', icon: Award }
  ];

  const whyChooseUs = [
    { 
      icon: Shield, 
      title: 'Govt Certified Programs', 
      description: 'All our programs are affiliated with Skill India, NSDC, and other government bodies'
    },
    { 
      icon: Briefcase, 
      title: 'Placement Support', 
      description: 'Dedicated placement assistance with 70%+ placement rate for trained candidates'
    },
    { 
      icon: BookOpen, 
      title: 'Industry-Relevant Courses', 
      description: 'Curriculum designed with industry experts to meet current market demands'
    },
    { 
      icon: MapPin, 
      title: 'Pan-India Network', 
      description: 'Strong network of 100+ training centers across multiple states'
    }
  ];

  const affiliationLogos = [
    { name: 'Skill India' },
    { name: 'NSDC' },
    { name: 'MSDE (NAPS)' },
    { name: 'ESSCI' },
    { name: 'TSSC' },
    { name: 'MSME' },
    { name: 'Startup India' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* HERO SECTION - Impact + Emotion */}
      <section className="relative overflow-hidden" data-testid="about-hero">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700" />
        
        {/* Pattern Overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Empowering Skills.<br />
              <span className="text-blue-200">Transforming Lives.</span><br />
              Building India's Future.
            </h1>
            <p className="text-lg sm:text-xl text-blue-100 max-w-3xl mx-auto mb-10">
              15+ years of excellence in skill development, training 25,000+ students and creating career opportunities across India.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/courses">
                <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50 font-bold px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all">
                  <GraduationCap className="w-5 h-5 mr-2" />
                  Explore Courses
                </Button>
              </Link>
              <Link to="/partner">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 font-bold px-8 py-6 text-lg rounded-xl">
                  <Building2 className="w-5 h-5 mr-2" />
                  Become a Partner
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Wave Bottom */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* IMPACT STATS SECTION */}
      <section className="py-16 bg-white" data-testid="impact-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Our Impact
            </h2>
            <p className="text-lg text-slate-600">Numbers that speak for our commitment to skill development</p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {impactStats.map((stat, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-6 text-center border border-slate-100 hover:shadow-lg transition-all hover:-translate-y-1"
                data-testid={`impact-stat-${index}`}
              >
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-7 h-7 text-blue-600" />
                </div>
                <p className="text-3xl sm:text-4xl font-bold text-blue-600 mb-2">{stat.number}</p>
                <p className="text-slate-600 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR STORY - Short & Powerful */}
      <section className="py-20 bg-slate-50" data-testid="our-story">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
                Our Story
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Bridging the Gap Between Education & Employment
              </h2>
              <div className="space-y-4 text-lg text-slate-600 text-justify">
                <p>
                  <strong className="text-slate-800">Iqra Skills was founded in 2011</strong> with a mission to bridge the gap between education and employment through quality skill development programs.
                </p>
                <p>
                  From implementing government schemes like <strong className="text-blue-600">PMKVY, NULM, and ESDM</strong> to training 25,000+ candidates, we have built a strong ecosystem for skill development and employment generation.
                </p>
                <p>
                  Today, we continue to empower youth with industry-relevant skills and career opportunities, partnering with leading organizations and government bodies across India.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl opacity-20 blur-xl" />
              <img 
                src="https://customer-assets.emergentagent.com/job_skill-india-partner/artifacts/9e4sfpnk_499029090_29866837612930071_5496693785407116872_n.jpg" 
                alt="Iqra Skills Training Center"
                className="relative rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER SECTION - Premium Design */}
      <section className="py-20 bg-white" data-testid="founder-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl overflow-hidden shadow-2xl">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Text Content */}
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <span className="inline-block px-4 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm font-semibold mb-6 w-fit">
                  Founder Message
                </span>
                
                {/* Quote */}
                <div className="relative mb-8">
                  <Quote className="w-12 h-12 text-blue-500/30 absolute -top-2 -left-2" />
                  <p className="text-2xl sm:text-3xl font-bold text-white leading-relaxed pl-8">
                    Skill development is not just training, <span className="text-blue-400">it's transformation.</span>
                  </p>
                </div>

                <p className="text-slate-300 text-lg mb-6 text-justify">
                  With over 17 years of dedicated experience in skill development, I have witnessed the transformative power of quality training. Our mission is to create a skilled and employable workforce that drives economic growth.
                </p>

                <div className="flex items-center gap-4 mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    AW
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Mr. Ab Wahab</h3>
                    <p className="text-slate-400">Founder & CEO | MCA, MBA</p>
                    <p className="text-blue-400 text-sm">17+ Years Experience</p>
                  </div>
                </div>

                <Link to="/partner-apply">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-3 rounded-xl w-fit">
                    Join Our Mission <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>

              {/* Image */}
              <div className="relative bg-gradient-to-br from-blue-600/20 to-purple-600/20 flex items-center justify-center p-8 lg:p-12">
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent lg:hidden" />
                <img 
                  src="https://customer-assets.emergentagent.com/job_2f370b5d-868a-4240-92b5-92a3302f8c5a/artifacts/4cuhoexj_1.%20AB%20Wahab%20copy.png" 
                  alt="Mr. Ab Wahab - Founder & CEO"
                  className="relative rounded-2xl shadow-2xl max-w-md w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION & VISION - Modern Cards */}
      <section className="py-20 bg-slate-50" data-testid="mission-vision">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Our Purpose
            </h2>
            <p className="text-lg text-slate-600">Driving India's skill revolution with clear vision and purpose</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Mission Card */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-all group">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Our Mission
              </h3>
              <p className="text-lg text-slate-600">
                Provide high-quality skill training with employment opportunities, bridging the gap between education and industry requirements through innovative methodologies.
              </p>
            </div>

            {/* Vision Card */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-all group">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Eye className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Our Vision
              </h3>
              <p className="text-lg text-slate-600">
                Become India's most trusted skill development partner, empowering millions with market-relevant skills and creating a workforce that drives economic growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* OUR JOURNEY - Timeline with Animation */}
      <section className="py-20 bg-white" data-testid="milestones" ref={timelineRef}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-4">
              15+ Years of Excellence
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Our Journey
            </h2>
            <p className="text-lg text-slate-600">A timeline of milestones that shaped our legacy</p>
          </div>
          
          {/* Timeline */}
          <div className="relative">
            {/* Center line */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-blue-500 via-purple-500 to-emerald-500 rounded-full" />
            
            <div className="space-y-6">
              {milestones.map((milestone, index) => {
                const Icon = milestone.icon;
                return (
                  <div 
                    key={index} 
                    className={`timeline-item relative flex items-center opacity-0 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                    style={{ 
                      animationDelay: `${index * 100}ms`,
                      animationFillMode: 'forwards'
                    }}
                    data-testid={`milestone-${index}`}
                  >
                    {/* Content */}
                    <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:pr-8 md:text-right' : 'md:pl-8 md:text-left'}`}>
                      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow-lg transition-all hover:-translate-y-1">
                        <div className={`flex items-center gap-3 mb-2 ${index % 2 === 0 ? 'md:flex-row-reverse' : ''}`}>
                          <span className="inline-flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
                            <Icon className="w-5 h-5 text-blue-600" />
                          </span>
                          <span className="px-3 py-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-bold rounded-full">
                            {milestone.year}
                          </span>
                        </div>
                        <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {milestone.title}
                        </h3>
                        <p className="text-sm text-slate-600 mt-1">{milestone.description}</p>
                      </div>
                    </div>
                    
                    {/* Center dot */}
                    <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 w-5 h-5 bg-white rounded-full border-4 border-blue-500 shadow-lg z-10" />
                    
                    {/* Empty space for other side */}
                    <div className="hidden md:block w-5/12" />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* AFFILIATIONS - Logo Grid */}
      <section className="py-16 bg-slate-50" data-testid="affiliations">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Recognized & Affiliated
            </h2>
            <p className="text-lg text-slate-600">Trusted by leading government organizations</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4 items-center">
            {affiliationLogos.map((logo, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-blue-50 to-emerald-50 rounded-xl p-4 flex items-center justify-center h-20 shadow-sm border border-slate-100 hover:shadow-lg hover:scale-105 transition-all"
                data-testid={`affiliation-logo-${index}`}
              >
                <span className="text-slate-700 font-bold text-sm text-center">{logo.name}</span>
              </div>
            ))}
          </div>

          {/* Additional Affiliations Text */}
          <div className="mt-12 bg-white rounded-2xl p-8 shadow-lg border border-slate-100">
            <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">Our Registrations & Partnerships</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                'Skill India - Affiliated Training Partner',
                'NSDC - Mobilization & Placement Partner',
                'MSDE - TPA for NAPS Program',
                'ESSCI - Affiliated Training Partner',
                'TSSC - Affiliated Training Partner',
                'Companies Act 2013 - Registered',
                'MSME Registered',
                'Startup India Recognized'
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span className="text-slate-700 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-20 bg-white" data-testid="why-choose-us">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold mb-4">
              Why Iqra Skills
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Why Choose Us
            </h2>
            <p className="text-lg text-slate-600">What sets us apart in skill development</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-6 border border-slate-100 hover:shadow-xl transition-all hover:-translate-y-2 group"
                data-testid={`why-choose-${index}`}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 relative overflow-hidden" data-testid="final-cta">
        {/* Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Start Your Journey with Iqra Skills Today
          </h2>
          <p className="text-xl text-blue-100 mb-10">
            Join India's growing skill development network and transform your career
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/courses">
              <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50 font-bold px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all w-full sm:w-auto">
                <GraduationCap className="w-5 h-5 mr-2" />
                Enroll Now
              </Button>
            </Link>
            <Link to="/partner-apply">
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all w-full sm:w-auto">
                <Building2 className="w-5 h-5 mr-2" />
                Open a Training Center
              </Button>
            </Link>
            <Link to="/jobs">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 font-bold px-8 py-6 text-lg rounded-xl w-full sm:w-auto">
                <Briefcase className="w-5 h-5 mr-2" />
                Hire Skilled Students
              </Button>
            </Link>
          </div>

          <p className="text-blue-200 mt-8 text-sm">
            <Clock className="w-4 h-4 inline mr-1" />
            Join 25,000+ students who have transformed their careers
          </p>
        </div>
      </section>

      <Footer />
      <Chatbot />

      {/* CSS for animations */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default AboutPage;
